import { useRef, useEffect, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { t } from 'i18next';
import Location from 'src/assets/icons/location.svg?react'
import { useMediaQuery } from 'src/utilities/hooks';
import CardAddressInput from 'src/components/CardAddressInput';

interface Props {
    onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
    value?: string | readonly string[] | number | undefined;
    onInputBlur?: (value: string) => void
    distanceError: boolean,
    errors: boolean
}

export const PlaceSelectCardInput = ({ onPlaceSelect, value, onInputBlur, distanceError, errors }: Props) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const inputRef = useRef<{ clearInputValue: () => void; inputElement: HTMLInputElement | null } | null>(null);
    const places = useMapsLibrary('places');
    const { isMobile } = useMediaQuery();

    const [aptNumber, setAptNumber] = useState(0);
    const [apartmentExists, setApartmentExists] = useState(true);
    const [currentPlace, setCurrentPlace] = useState<google.maps.places.PlaceResult | null>(null);

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const options = {
            fields: ['geometry', 'name', 'formatted_address', 'address_components']
        };

        if (!inputRef.current.inputElement) return;
        setPlaceAutocomplete(new places.Autocomplete(inputRef.current.inputElement!, options));
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            const place = placeAutocomplete.getPlace();
            setCurrentPlace(place);
            const aptField = place.address_components?.filter(x => x.types?.includes('subpremise'))[0]

            if (aptField) {
                setAptNumber(parseInt(aptField.long_name));
                setApartmentExists(true);
            }
            else {
                setApartmentExists(false);
            }

            onPlaceSelect(place);
        });
    }, [placeAutocomplete, aptNumber]);

    const onApartmentChange = ((value: number) => {

        setAptNumber(value);

        if (!currentPlace) {
            return;
        }

        if (value > 0) {
            const aptField = currentPlace?.address_components?.filter(x => x.types?.includes('subpremise'))[0]

            const newAptField = {
                long_name: value.toString(),
                short_name: value.toString(),
                types: ['subpremise']
            } as google.maps.GeocoderAddressComponent;

            if (currentPlace) {
                if (aptField) {
                    const index = currentPlace.address_components?.indexOf(aptField);
                    if (index !== undefined && index > -1) {
                        currentPlace.address_components![index] = newAptField
                    }
                }
                else {
                    currentPlace.address_components?.push(newAptField);
                }
                onPlaceSelect(currentPlace);
            }
        }
        else {
            const aptField = currentPlace?.address_components?.filter(x => x.types?.includes('subpremise'))[0]

            if (currentPlace && aptField) {
                const index = currentPlace.address_components?.indexOf(aptField);
                if (index !== undefined && index > -1) {
                    currentPlace.address_components?.splice(index, 1);
                    onPlaceSelect(currentPlace);
                }
            }
        }
    })

    return (
        <CardAddressInput
            hideShadow={isMobile}
            value={value}
            label={t('pages.deliveryData.form.addressTitle')}
            inputRequired
            Icon={Location}
            ref={inputRef}
            onInputBlur={onInputBlur}
            onApartmentChange={onApartmentChange}
            forceDesktop
            errorText={distanceError ? t('pages.deliveryData.form.distanceError') : undefined}
            error={errors}
            needApartment={!apartmentExists}
        />
    );
};